// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/estoque/hooks/useMovimentacao.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { movimentacaoService, type FiltrosMovimentacao } from '../services/movimentacaoService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarMovimentacaoRequest } from '../types';

const KEYS = {
  all:    ['movimentacoes-estoque'] as const,
  lista:  (pagina: number, tamanhoPagina: number, tipoId: number, filtros: FiltrosMovimentacao) =>
    ['movimentacoes-estoque', 'lista', pagina, tamanhoPagina, tipoId, filtros] as const,
  detalhe: (id: number) => ['movimentacoes-estoque', id] as const,
  tipos:  ['movimentacoes-estoque', 'tipos'] as const,
};

interface UseMovimentacaoParams {
  pagina?: number;
  tamanhoPagina?: number;
  tipoId?: number;
  /** Busca, período e ordenação (servidor). */
  filtros?: FiltrosMovimentacao;
}

export const useMovimentacao = ({
  pagina = 1,
  tamanhoPagina = 20,
  tipoId = 0,
  filtros = {},
}: UseMovimentacaoParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS.lista(pagina, tamanhoPagina, tipoId, filtros),
    queryFn:  () => movimentacaoService.getAll(pagina, tamanhoPagina, tipoId, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });

  const movimentacoes = paginado?.itens       ?? [];
  const total         = paginado?.total        ?? 0;
  const totalPaginas  = paginado?.totalPaginas ?? 1;

  const createMutation = useMutation({
    mutationFn: (req: CriarMovimentacaoRequest) => movimentacaoService.create(req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Movimentação registrada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => movimentacaoService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Movimentação excluída com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    movimentacoes,
    total,
    totalPaginas,
    isLoading,
    isFetching,
    create:      createMutation.mutateAsync,
    deleteMovimentacao: deleteMutation.mutate,
    isCreating:  createMutation.isPending,
  };
};

export const useMovimentacaoDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS.detalhe(id),
    queryFn:  () => movimentacaoService.getById(id),
    enabled:  id > 0,
  });

export const useTiposMovimento = () =>
  useQuery({
    queryKey: KEYS.tipos,
    queryFn:  movimentacaoService.getTipos,
    staleTime: Infinity,
  });
